// Szkic w localStorage: autozapis (debounce 500 ms), przywracanie po
// powrocie, wygasanie po 30 dniach, czyszczenie po wysyłce. Pliki
// pomijamy (File nie przeżyje serializacji). Wszystko w try/catch —
// Safari private mode rzuca przy zapisie.
//
// Autozapis MUSI być widoczny: badania NN/g pokazują, że użytkownicy
// zakładają, iż nic nie jest zapisane, dopóki nie kliknęli „Zapisz".
// Stąd pill ze stanami saving/saved/idle, z opóźnieniem pokazania i
// minimalnym czasem widoczności — inaczej przy szybkim zapisie mrugnie
// i autozapis nie jest odczuwalny, co dla klienta znaczy tyle, co brak.
//
// Przywracanie nie jest ciche: pytamy „Kontynuuj / Zacznij od nowa".
// Ciche wracanie do cudzych danych na współdzielonym komputerze to
// problem prywatności, nie udogodnienie.

import { HONEYPOT, DRAFT_KEY as KEY, DRAFT_MAX_AGE_DAYS as MAX_AGE_DAYS } from "./config.js";

const SHOW_DELAY = 200;
const MIN_VISIBLE = 450;

const SVG_CHECK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
const SVG_SPIN =
    '<svg class="briefSave__spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.2-8.6"/></svg>';
const SVG_ALERT =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5M12 16.4v.01"/></svg>';

function readStorage() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (!draft || draft.v !== 2) return null;
        if (Date.now() - draft.savedAt > MAX_AGE_DAYS * 24 * 3600 * 1000) return null;
        return draft;
    } catch {
        return null;
    }
}

export function clearDraft() {
    try {
        localStorage.removeItem(KEY);
    } catch {
        /* brak dostępu do storage — trudno */
    }
}

function relativeTime(timestamp) {
    const minutes = Math.round((Date.now() - timestamp) / 60000);
    if (minutes < 1) return "przed chwilą";
    if (minutes === 1) return "minutę temu";
    if (minutes < 5) return minutes + " minuty temu";
    if (minutes < 60) return minutes + " minut temu";
    const date = new Date(timestamp);
    return "o " + String(date.getHours()).padStart(2, "0") + ":" + String(date.getMinutes()).padStart(2, "0");
}

export function initDraft(form, api) {
    let timer = null;
    let savedAt = 0;

    const status = document.querySelector("[data-save-status]");
    const statusText = status ? status.querySelector("[data-save-text]") : null;
    let showTimer = null;
    let doneTimer = null;
    let idleTimer = null;

    function setStatus(state, text, icon) {
        if (!status || !statusText) return;
        status.dataset.state = state;
        const svg = status.querySelector("svg");
        if (svg) svg.outerHTML = icon;
        statusText.textContent = text;
    }

    function statusIdle() {
        setStatus("idle", savedAt ? "Zapisano " + relativeTime(savedAt) : "Zapisujemy na bieżąco", SVG_CHECK);
    }

    function statusSaving() {
        clearTimeout(showTimer);
        clearTimeout(doneTimer);
        clearTimeout(idleTimer);
        showTimer = setTimeout(() => {
            setStatus("saving", "Zapisywanie…", SVG_SPIN);
            doneTimer = setTimeout(() => {
                setStatus("saved", "Zapisano", SVG_CHECK);
                idleTimer = setTimeout(statusIdle, 2000);
            }, MIN_VISIBLE);
        }, SHOW_DELAY);
    }

    function statusError() {
        clearTimeout(showTimer);
        clearTimeout(doneTimer);
        clearTimeout(idleTimer);
        setStatus("error", "Nie udało się zapisać kopii", SVG_ALERT);
    }

    function collect() {
        const fields = {};
        form.querySelectorAll("input, textarea, select").forEach((input) => {
            if (input.type === "file" || input.name === "" || input.name === HONEYPOT) return;
            if (input.type === "radio") {
                if (input.checked) fields[input.name] = input.value;
                return;
            }
            if (input.type === "checkbox") {
                if (!fields[input.name]) fields[input.name] = [];
                if (input.checked) fields[input.name].push(input.value);
                return;
            }
            fields[input.name] = input.value;
        });
        return fields;
    }

    function save() {
        statusSaving();
        try {
            savedAt = Date.now();
            localStorage.setItem(
                KEY,
                JSON.stringify({
                    v: 2,
                    savedAt,
                    step: api.steps ? api.steps.getCurrent() : 0,
                    rozsz: api.conditionals ? api.conditionals.isRozsz() : false,
                    fields: collect(),
                })
            );
        } catch {
            statusError();
        }
    }

    function restore(draft) {
        Object.entries(draft.fields || {}).forEach(([name, value]) => {
            const inputs = form.querySelectorAll('[name="' + CSS.escape(name) + '"]');
            inputs.forEach((input) => {
                if (input.type === "radio") {
                    input.checked = input.value === value;
                } else if (input.type === "checkbox") {
                    input.checked = Array.isArray(value) && value.includes(input.value);
                } else {
                    input.value = value == null ? "" : value;
                }
            });
        });
        if (draft.rozsz && api.conditionals) api.conditionals.setRozsz(true, false);
        if (api.conditionals) api.conditionals.settle();
    }

    const draft = readStorage();
    if (draft && Object.keys(draft.fields || {}).length > 0) {
        restore(draft);
        savedAt = draft.savedAt || 0;

        const notice = document.querySelector("[data-draft-notice]");
        const summary = document.querySelector("[data-draft-summary]");
        const continueBtn = document.querySelector("[data-draft-continue]");
        const resumeStep = Math.max(1, Number(draft.step) || 1);

        if (summary) {
            const done = Math.max(0, resumeStep - 1);
            summary.textContent =
                "Zapisane " + relativeTime(savedAt) + " · ukończono " + done +
                " z " + (api.steps ? api.steps.getTotal() : 8) + " kroków.";
        }
        if (notice) notice.hidden = false;
        if (continueBtn && api.steps) {
            continueBtn.addEventListener("click", () => api.steps.goTo(resumeStep));
        }
        statusIdle();
    }

    const resetBtn = document.querySelector("[data-draft-reset]");
    if (resetBtn) {
        resetBtn.addEventListener("click", () => {
            clearDraft();
            window.location.hash = "";
            window.location.reload();
        });
    }

    form.addEventListener("input", () => {
        clearTimeout(timer);
        timer = setTimeout(save, 500);
    });
    form.addEventListener("change", () => {
        clearTimeout(timer);
        timer = setTimeout(save, 500);
    });

    // odświeżanie względnego czasu w tle — to on buduje zaufanie w długiej sesji
    setInterval(() => {
        if (status && status.dataset.state === "idle" && savedAt) statusIdle();
    }, 30000);

    return { save, clearDraft };
}
